/**
 * SEO Component
 *
 * Reusable component for managing meta tags with i18n support.
 * Uses react-helmet-async for dynamic head management.
 */

import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SEO_CONFIG } from '../lib/config';

interface SEOProps {
  /** Page title (used as-is, should include site name if desired) */
  title?: string;
  /** Meta description */
  description?: string;
  /** Canonical path (without base URL, e.g., '/book-call') */
  path?: string;
  /** Open Graph image path (relative to base URL) */
  ogImage?: string;
  /** Alt text for OG image */
  ogImageAlt?: string;
  /** Structured data (JSON-LD) */
  structuredData?: object | object[];
  /** Page type for OG */
  ogType?: 'website' | 'article';
  /** Prevent indexing */
  noIndex?: boolean;
}

export function SEO({
  title,
  description,
  path = '',
  ogImage,
  ogImageAlt,
  structuredData,
  ogType = 'website',
  noIndex = false,
}: SEOProps) {
  const { i18n } = useTranslation('seo');
  const isArabic = i18n.language === 'ar';

  // Build URLs
  const baseUrl = SEO_CONFIG.baseUrl;
  const canonicalUrl = `${baseUrl}${path}`;

  // Determine alternate URLs based on current language
  const enPath = path.replace(/^\/ar/, '') || '/';
  const arPath = path.startsWith('/ar') ? path : `/ar${path || ''}`;

  // Use provided alternatePath or compute it
  const enUrl = `${baseUrl}${enPath === '/' ? '' : enPath}`;
  const arUrl = `${baseUrl}${arPath}`;
  const xDefaultUrl = enUrl; // x-default points to English

  // OG Image - use language-specific default if not provided
  const defaultImage = isArabic
    ? SEO_CONFIG.defaultOgImageAr
    : SEO_CONFIG.defaultOgImage;
  const ogImageUrl = `${baseUrl}${ogImage || defaultImage}`;

  // Default image alt text
  const defaultOgImageAlt = isArabic
    ? 'Saqr Skills - منصة تقييم صوتي بالذكاء الاصطناعي'
    : 'Saqr Skills - AI-Powered Voice Assessment Platform';

  // Use title as-is (titles should include site name branding)
  const fullTitle = title || SEO_CONFIG.siteName;

  // Locale for OG
  const ogLocale = isArabic ? 'ar_SA' : 'en_US';
  const ogLocaleAlternate = isArabic ? 'en_US' : 'ar_SA';

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical and Hreflang */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="ar" href={arUrl} />
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlternate} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content={ogImageAlt || defaultOgImageAlt}
      />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImageUrl} />
      <meta
        name="twitter:image:alt"
        content={ogImageAlt || defaultOgImageAlt}
      />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(structuredData) ? structuredData : [structuredData]
          )}
        </script>
      )}
    </Helmet>
  );
}
